import i18next, { TFunction } from 'i18next'
import {
  IBankBrands,
  IBankClabeName,
  IEmoji,
  IEnvironmentMode,
  IIdentitySigninRedirectStateValues,
  IImage,
  ILabelComponents,
  IPaymentBrandLogos,
  IPaymentBrands,
  IPaymentStatusColors,
  THttpResponseStatusTypeColors,
  TSecuenceSteps
} from './interface'
import { IOptionType } from 'components/Form/interface'
import { IPaymentLinksPagination } from 'context/interface'
import { IToastAlert, ToastAlertType } from 'components/ToastAlert/interface'
import { Label, LabelPlaceholder } from '@conekta/ui-component-library'

const T_COUNTRY = 'country'

export const API_PAYMENTS_URL = process.env.API_PAYMENTS_URL

export const APP_LANGUAGE_CODE_DEFAULT = 'es'

export const APP_NAME = {
  onboarding: 'onboarding',
  panel: 'panel'
}

export const APP_TITLE = 'Conekta'

export const APP_HELP_CENTER_URL_BASE = {
  [APP_NAME.onboarding]: 'https://help.conekta.com/hc/es-419',
  [APP_NAME.panel]: 'https://help.conekta.com/hc/es-419'
}

export const APP_WEBSITE_URL_BASE = {
  [APP_NAME.onboarding]: 'https://www.conekta.com',
  [APP_NAME.panel]: 'https://www.conekta.com'
}

export const APP_WEBSITE_URL_WEBPAGES = {
  [APP_NAME.onboarding]: {
    pricing: `${APP_WEBSITE_URL_BASE[APP_NAME.onboarding]}/pricing`,
    termsOfService: `${APP_WEBSITE_URL_BASE[APP_NAME.onboarding]}/legal/tos`,
    privacyPolicy: ''
  },
  [APP_NAME.panel]: {
    pricing: `${APP_WEBSITE_URL_BASE[APP_NAME.panel]}/pricing`,
    termsOfService: `${APP_WEBSITE_URL_BASE[APP_NAME.panel]}/legal/tos`,
    privacyPolicy: `${APP_WEBSITE_URL_BASE[APP_NAME.panel]}/legal/privacy`
  }
}

export const AWS_S3_URL = 'https://assets.conekta.com/cpanel/statics/assets'
export const URL_ICONS = `${AWS_S3_URL}/img/icons/`
export const URL_IMAGES = `${AWS_S3_URL}/img/`

export const BANK_BRAND_CLABE_NAME: IBankClabeName = {
  '002': 'citi_banamex',
  '012': 'bbva',
  '014': 'santander',
  '021': 'hsbc',
  '030': 'banco_bajio',
  '036': 'inbursa',
  '044': 'scotiabank',
  '058': 'banregio',
  '072': 'banorte',
  '127': 'banco_azteca',
  _default: 'banco_generico'
}

export const BANK_BRAND_FULL_NAMES: IBankBrands = {
  banco_azteca: 'Banco Azteca',
  banco_bajio: 'Banco Bajío',
  banco_generico: 'Banco Genérico',
  banorte: 'Banorte',
  banregio: 'Banregio',
  bbva: 'BBVA',
  citi_banamex: 'Citi Banamex',
  hsbc: 'HSBC',
  inbursa: 'Inbursa',
  santander: 'Santander',
  scotiabank: 'Scotiabank'
}

export const BANK_BRAND_LOGOS: IBankBrands = {
  banco_azteca: `${AWS_S3_URL}/img/logos-bank/banco-azteca.png`,
  banco_bajio: `${AWS_S3_URL}/img/logos-bank/banco-bajio.png`,
  banco_generico: `${AWS_S3_URL}/img/logos-bank/banco-generico.png`,
  banorte: `${AWS_S3_URL}/img/logos-bank/banorte.png`,
  banregio: `${AWS_S3_URL}/img/logos-bank/banregio.png`,
  bbva: `${AWS_S3_URL}/img/logos-bank/bbva.png`,
  citi_banamex: `${AWS_S3_URL}/img/logos-bank/citi-banamex.png`,
  hsbc: `${AWS_S3_URL}/img/logos-bank/hsbc.png`,
  inbursa: `${AWS_S3_URL}/img/logos-bank/inbursa.png`,
  santander: `${AWS_S3_URL}/img/logos-bank/santander.png`,
  scotiabank: `${AWS_S3_URL}/img/logos-bank/scotiabank.png`
}

export const BUILD_COMPANY_ADMIN_REDIRECT_URL = (
  adminRedirectUrl = '',
  entityId: string,
  idReferenceCompany = '',
  section = ''
) => {
  const ADMIN_REDIRECT_URL = adminRedirectUrl || ''
  const AT_CODE = idReferenceCompany ? `&at_code=${idReferenceCompany}` : ''
  const ENTITY_ID = entityId ? `&entity_id=${entityId}` : ''
  const SECTION = section ? `&section=${section}` : ''

  return `${ADMIN_REDIRECT_URL}${AT_CODE}${ENTITY_ID}${SECTION}`
}

export const CHARGE_STATUS: any = {
  color: {
    charged_back: 'red',
    declined: 'red',
    fraudulent: 'orange',
    paid: 'green',
    partially_refunded: 'blue',
    pending_payment: 'yellow',
    pre_authorized: 'yellow',
    refunded: 'blue',
    voided: 'red'
  },
  declined: {
    declined: 'declined',
    pendingPayment: 'pending_payment'
  },
  failure: {
    alreadyPaid: 'charge_is_already_paid',
    amountNotAllowed: 'amount_not_allowed',
    canceled: 'charge_is_canceled',
    canceledReference: 'canceled_reference',
    expired: 'charge_is_expired',
    expiredReference: 'expired_reference',
    generalError: 'general_error',
    inactiveReference: 'inactive_reference',
    invalidAmount: 'invalid_amount',
    merchantAfterHours: 'merchant_after_hours',
    merchantNotAvailable: 'merchant_not_available',
    notAuthorized: 'not_authorized',
    paymentAlreadyReversed: 'payment_already_reversed',
    paymentInProcess: 'payment_in_process',
    referenceNotFound: 'reference_not_found',
    referenceWithoutDebt: 'reference_without_debt',
    reverseNotExecuted: 'reversed_not_executed',
    userBlocked: 'user_blocked'
  },
  fraudulent: {
    declined: 'declined_by_3ds',
    suspected: 'suspected_fraud'
  },
  payment: {
    card: 'card_payment',
    cash: 'cash_payment'
  }
}

export const COMPONENT_LABELS: ILabelComponents = {
  default: Label,
  labelPlaceholder: LabelPlaceholder
}

export const CONEKTA_PAYMENT_MIN_MAX_AMOUNT = {
  card: { minimum: 3 }
}

export const CONTACT_CONEKTA = {
  sales_email: 'ventas@conekta.com',
  sales_phone: '55 41 61 35 35',
  support_email: 'soporte@conekta.com',
  website_url: 'www.conekta.com'
}

export const COUNTRY_OPTION_DEFAULT = (): IOptionType => ({ label: t(`${T_COUNTRY}.mx`), value: 'mx' })

export const COUNTRY_OPTIONS = (): IOptionType[] => [
  COUNTRY_OPTION_DEFAULT(),
  {
    label: t(`${T_COUNTRY}.ca`),
    value: 'ca'
  },
  {
    label: t(`${T_COUNTRY}.us`),
    value: 'us'
  },
  {
    label: t(`${T_COUNTRY}.co`),
    value: 'co'
  }
]

export const CURRENCY_MEXICAN_PESO = 'MXN'
export const CURRENCY_US_DOLLAR = 'USD'

export const DATE_FORMAT_LL = 'LL' // Only for Moment

export const DATE_FORMAT_P = 'P' // 'L' in Moment
export const DATE_FORMAT_PP = 'PP' // 'll' in Moment
export const DATE_FORMAT_PPP = 'PPP' // 'LL' in Moment
export const DATE_FORMAT_SHORT_DATE_AND_TIME = 'P, hh:mm aaaa' // 'L, LT' in Moment
export const DATE_FORMAT_LONG_DATE_AND_TIME = 'PP h:mm aaaa' // 'LLL' in Moment

export const DATE_INPUT_FORMAT = 'yyyy-MM-dd' // 'YYYY-MM-DD' in Moment
export const DATE_FORMAT_FILTER = 'dd/MM/yyyy' // 'DD/MM/YYYY' in Moment
export const PERIOD_FORMAT_FILTER = 'MM/yyyy' // 'MM/YYYY' in Moment
export const DATE_TIME_FORMAT = 'dd/MM/yyyy, h:mm aa'
export const TIME_FORMAT = 'h:mm aa'

export const DEFAULT_NUMBER_OF_PAGES_REQUESTED_TO_ENDPOINTS = 20

export const DEV_CENTER_URL = process.env.APP_DEV_CENTER_URL

// Contract ESign
export const EDOCSIGNPROVIDER_TEMPLATE_ID = process.env.EDOCSIGNPROVIDER_TEMPLATE_ID
export const EDOCSIGNPROVIDER_TOKEN = process.env.EDOCSIGNPROVIDER_TOKEN
export const EDOCSIGNPROVIDER_URL = process.env.EDOCSIGNPROVIDER_URL

export const EMOJI: IEmoji = {
  events: ['💥'],
  fruits: ['🍅', '🍇', '🍈', '🍉', '🍊', '🍋', '🍌', '🍍', '🍎', '🍏', '🍐', '🍑', '🍒', '🍓', '🥝', '🥥', '🥭']
}

export const ENVIRONMENT_MODE: IEnvironmentMode = {
  production: 'Production',
  sandbox: 'Sandbox'
}

export const FEATURE_MANAGEMENT_PROVIDER_CLIENT_ID: string | undefined =
  process.env.FEATURE_MANAGEMENT_PROVIDER_CLIENT_ID

export const HTTP_RESPONSE_STATUS_OK = '200'
export const HTTP_RESPONSE_STATUS_BAD_REQUEST = '400'
export const HTTP_RESPONSE_STATUS_SERVICE_UNAVAILABLE = '503'

export const HTTP_RESPONSE_STATUS_CODE: Record<string, number> = {
  [HTTP_RESPONSE_STATUS_OK]: 200, // OK
  '201': 201, // Created
  '204': 204, // OK without content,
  '404': 404, // Not found
  '500': 500, // Internal server error
  '503': 503, // Internal server error
  '504': 504 // Gateway timeout
}

export const HTTP_RESPONSE_STATUS_CODE_STRING: Record<string, string> = {
  [HTTP_RESPONSE_STATUS_OK]: '200',
  [HTTP_RESPONSE_STATUS_BAD_REQUEST]: '400',
  [HTTP_RESPONSE_STATUS_SERVICE_UNAVAILABLE]: '503'
}

export const HTTP_RESPONSE_STATUS_TYPE_COLORS: THttpResponseStatusTypeColors = {
  clientError: 'orangeDark',
  informational: 'yellow',
  redirection: 'blueDark',
  serverError: 'redDark',
  success: 'green',
  unknown: 'gray'
}

export const LOCAL_STORAGE_APP_CONTEXT_DATA_VAR_NAME = 'conektaAppContextData'
export const LOCAL_STORAGE_COMPANY = 'company'
export const LOCAL_STORAGE_CURRENT_COMPANY_REFERENCE = '_currentCompany'
export const LOCAL_STORAGE_I18N_VAR_NAME = 'conektaI18NextLng'
export const LOCAL_STORAGE_NAVIGATION_EXPANDED_VAR_NAME = 'navigationExpanded'
export const LOCAL_STORAGE_SELECTED_COMPANY_VAR_NAME = 'selectedCompany'
export const LOCAL_STORAGE_USER = 'user'
export const LOCAL_STORAGE_USER_SESSION_VAR_NAME = 'conektaIdentityServerUser'
export const LOCAL_STORAGE_WIZARD_DATA = 'wizardData'
export const LOCAL_STORAGE_WIZARD_SAVED_DATA = 'wizardSavedData'
export const LOCAL_STORAGE_NOTIFICATIONS = 'notifications'
export const LOCAL_STORAGE_TOUR_ANTIFRAUD_ENABLED_KEY = 'antifraudTourEnabled'
export const LOCAL_STORAGE_DOWNLOAD_MANAGER_KEY = 'conektaDownloadManager'
export const LOCAL_STORAGE_ACCOUNT_STATUS_APPROVED_KEY = 'accountStatusApproved'
export const LOCAL_STORAGE_NEW_LABEL_NOTIFICATION_BELL = 'newLabelNotificationBell'

export const LOCAL_STORAGE_REPRESENTATION_BANNER_KEY = 'representationBannerWarning'
export const LOCAL_STORAGE_TRANSFERS_BANNER_KEY = 'transfersBannerInfo'

const IDENTITY_CONF_URL_REDIR =
  window.location.origin === process.env.IDENTITY_CONF_URL_REDIR
    ? process.env.IDENTITY_CONF_URL_REDIR
    : process.env.IDENTITY_CONF_URL_REDIR_BETA
const IDENTITY_CONF_URL_REDIR_DIR = process.env.IDENTITY_CONF_URL_REDIR_DIR
const IDENTITY_CONF_URL_SERVER = process.env.IDENTITY_CONF_URL_SERVER
const IDENTITY_CONF_URL_SILENT = process.env.IDENTITY_CONF_URL_SILENT_REDIRECT
export const FLAG_COMPANY_MIGRATED = process.env.FLAG_COMPANY_MIGRATED === 'true'
export const IDENTITY_SERVER_CONFIG_VALUES = {
  authority: IDENTITY_CONF_URL_SERVER,
  automaticSilentRenew: false,
  client_id: process.env.IDENTITY_CONF_CLIENT_ID,
  clockSkew: 900,
  post_logout_redirect_uri: IDENTITY_CONF_URL_REDIR,
  redirect_uri: `${IDENTITY_CONF_URL_REDIR}${IDENTITY_CONF_URL_REDIR_DIR}`,
  response_type: process.env.IDENTITY_CONF_RESPONSE_TYPE,
  scope: process.env.IDENTITY_CONF_SCOPE,
  silent_redirect_uri: `${IDENTITY_CONF_URL_REDIR}${IDENTITY_CONF_URL_SILENT}`
}
export const IDENTITY_CONF_OIDC_METADATA_VALUES_REQUESTED = {
  authorization_endpoint: `${IDENTITY_CONF_URL_SERVER}/connect/authorize`,
  check_session_iframe: `${IDENTITY_CONF_URL_SERVER}/connect/checksession`,
  end_session_endpoint: `${IDENTITY_CONF_URL_SERVER}/connect/endsession`,
  introspection_endpoint: `${IDENTITY_CONF_URL_SERVER}/connect/introspect`,
  issuer: process.env.IDENTITY_CONF_URL_SERVER,
  jwks_uri: `${IDENTITY_CONF_URL_SERVER}/.well-known/openid-configuration/jwks`,
  revocation_endpoint: `${IDENTITY_CONF_URL_SERVER}/connect/revocation`,
  token_endpoint: `${IDENTITY_CONF_URL_SERVER}/connect/token`,
  userinfo_endpoint: `${IDENTITY_CONF_URL_SERVER}/connect/userinfo`
}

export const IDENTITY_SIGNIN_REDIRECT_DEFAULT_STATE_VALUES: IIdentitySigninRedirectStateValues = {
  from: { ...window.location, pathname: '/' }
}

// Can't be ordered alphabetically due to its dependency
// with the "LOCAL_STORAGE_NAVIGATION_EXPANDED_VAR_NAME" variable
export const INITIAL_LOCAL_STORAGE_APP_STATE = {
  [LOCAL_STORAGE_NAVIGATION_EXPANDED_VAR_NAME]: false,
  [LOCAL_STORAGE_NOTIFICATIONS]: []
}

export const IMG_ADD_CIRCLE = `${AWS_S3_URL}/img/icons/add-circle-24px.svg`
export const IMG_ADD_ITEM = `${AWS_S3_URL}/img/icons/add-item-24px.svg`
export const IMG_ARROW_DROP_DOWN_BLUE = `${AWS_S3_URL}/img/chevron-my-company.svg`
export const IMG_ARROW_DROP_DOWN_WHITE = `${AWS_S3_URL}/img/icons/arrow-drop-down-white-24px.svg`
export const IMG_CHECK_MARK = `${AWS_S3_URL}/img/icons/check-mark-24px.svg`
export const IMG_COMPANY = `${AWS_S3_URL}/img/tesla.jpg`
export const IMG_DELETE_BLACK_OUTLINE = `${AWS_S3_URL}/img/icons/delete_black%20outline_24px.svg`
export const IMG_DELETE_GREY = `${AWS_S3_URL}/img/icons/delete-gray-24px.svg`
export const IMG_DOCUMENT_SYMBOL = `${AWS_S3_URL}/img/doc-my-company.svg`
export const IMG_EDIT_SNOWBERRY_24 = `${AWS_S3_URL}/img/icons/edit-snowberry-24px.svg`
export const IMG_HELP = `${AWS_S3_URL}/img/nav/help-16px.svg`
export const IMG_HELP_QUESTION_MARK = `${AWS_S3_URL}/img/icons/help-color-24px.svg`
export const IMG_MAIN_LOGO_FULL = `${AWS_S3_URL}/img/conekta-logo-full.svg`
export const IMG_MAIN_LOGO_FULL_BLUE = `${AWS_S3_URL}/img/conekta-logo-blue-full.svg`
export const IMG_MAIN_LOGO_SYMBOL = `${AWS_S3_URL}/img/conekta-logo-symbol.svg`
export const IMG_MAIN_LOGO_SYMBOL_INVERSE = `${AWS_S3_URL}/img/conekta-logo-symbol-inverse.svg`
export const IMG_MORE_VERT = `${AWS_S3_URL}/img/icons/more-vert-24px.svg`
export const IMG_MORE_VERT_V3 = `${AWS_S3_URL}/img/icons/more-vert-24px-v3.svg`
export const IMG_USER = `${AWS_S3_URL}/img/robert-downey.jpg`
export const IMG_TRASH = `${AWS_S3_URL}/img/icons/trash.svg`
export const IMG_TRASH_BLUE = `${AWS_S3_URL}/img/icons/Trash_Blue.svg`
export const IMG_TRASH_NEUTRAL = `${AWS_S3_URL}/img/icons/trash_neutral.svg`
export const IMG_PENCIL = `${AWS_S3_URL}/img/icons/pencil.svg`
export const IMG_CHECK_CIRCLE_OUTLINE = `${AWS_S3_URL}/img/icons/check_circle_outline.svg`
export const IMG_INFO_OUTLINED = `${AWS_S3_URL}/img/icons/info-outlined.svg`
export const IMG_EMPTY_API_KEY = `${AWS_S3_URL}/img/api-keys-empty.png`
export const IMG_BULB_NEUTRAL_8 = `${AWS_S3_URL}/img/icons/light_bulb_neutral_8.svg`
export const IMG_BULB_YELLOW_5 = `${AWS_S3_URL}/img/icons/bulb_yellow_5.svg`
export const IMG_WARNING_OCTAGON_YELLOW_4 = `${AWS_S3_URL}/img/icons/warning-octagon-yellow-4.svg`
export const IMG_TOGGLE = `${AWS_S3_URL}/img/icons/Toggle.svg`
export const IMG_UPDATE_ICON_MODAL = `${AWS_S3_URL}/img/icons/YellowWarningOctagon.svg`
export const IMG_DELETE_ICON_MODAL = `${AWS_S3_URL}/img/icons/RedWarningOctagon.svg`
export const IMG_DELETE_ALERT = `${AWS_S3_URL}/img/icons/Warning.svg`
export const IMG_SUCCESS_ALERT = `${AWS_S3_URL}/img/icons/Check.svg`
export const IMG_WARNING_ALERT = `${AWS_S3_URL}/img/icons/WarningOctagon.svg`
export const IMG_SNACKBARS_API_KEY = `${AWS_S3_URL}/img/icons/InfoFilled.svg`
export const IMG_EMPTY_STATE = `${AWS_S3_URL}/img/EmptyState_Illustration.svg`
export const IMG_EDIT = `${AWS_S3_URL}/img/icons/edit-medium-outlined.svg`
export const IMG_COPY = `${AWS_S3_URL}/img/icons/copy-medium-outlined.svg`
export const IMG_FORM_WEBHOOKS = `${AWS_S3_URL}/img/ChatTeardropDots.svg`

export const IMG_DRAWER_CHARGERBACKS = `${AWS_S3_URL}/img/drawer-contracargos.svg`
export const IMG_DRAWER_ACCEPTANCE = `${AWS_S3_URL}/img/drawer-acceptance.svg`
export const IMG_DRAWER_ACCEPTANCE_GRAPHIC = `${AWS_S3_URL}/img/acceptance-graphic.svg`
export const IMG_DRAWER_ACCEPTANCE_GRAPHIC_ENGLISH = `${AWS_S3_URL}/img/acceptance-graphic-english.svg`
export const IMG_PIPELINE_DESKTOP = `${AWS_S3_URL}/img/pipeline-desktop.png`
export const IMG_LOAD_BANNER_ACTIVATION_PROCESS = `${AWS_S3_URL}/img/OB_Loading_Banner.svg`
export const IMG_ONB_PENDING_ACCOUNT_STATUS = `${AWS_S3_URL}/img/ONB_Pending_Account_Status.svg`
export const IMG_ONB_REQUIRED_DOCUMENTATION_STATUS = `${AWS_S3_URL}/img/ONB_Required_Documents_Status.svg`
export const IMG_ONB_APPROVED_STATUS = `${AWS_S3_URL}/img/ONB_Approved_Status.svg`
export const IMG_ONB_SANDBOX_ACCOUNT_STATUS = `${AWS_S3_URL}/img/ONB_Sandbox_Account_Status.svg`
export const IMG_INTEGRATION_PLUGINS = `${AWS_S3_URL}/img/Integration_Plugins.svg`
export const IMG_INTEGRATION_PAYMENT_LINK = `${AWS_S3_URL}/img/Integration_Payment_Link.svg`
export const IMG_INTEGRATION_PAYMENT_BUTTON = `${AWS_S3_URL}/img/Integration_Payment_Button.svg`
export const IMG_ONB_TEST_MODE = `${AWS_S3_URL}/img/Test_mode_toggle.svg`
export const IMG_PIPELINE_MOBILE = `${AWS_S3_URL}/img/pipeline-mobile.png`
export const IMG_KEY_MODAL = `${AWS_S3_URL}/img/Key.png`
export const IMG_WARNING = `${AWS_S3_URL}/img/warning.svg`
export const IMG_WARNING2 = `${AWS_S3_URL}/img/warning2.svg`
export const IMG_ADD = `${URL_ICONS}add-25px.svg`
export const IMG_3DS_FLAG = `${AWS_S3_URL}/img/3DS.png`
export const IMG_YELLOW_FLAG = `${AWS_S3_URL}/img/yellow-flag.png`
export const IMG_CASH_TOP_BAR = `${AWS_S3_URL}/img/hand-cash.png`
export const IMG_CASH_MIGRATED_MODAL = `${AWS_S3_URL}/img/hand-cash-black.png`
export const IMG_WARNING_FILLED = `${AWS_S3_URL}/img/lists/warning-filled-40x40.svg`
export const IMG_ANTIFRAUD_BLUE = `${AWS_S3_URL}/img/icons/antifraud-filled-blue-24px.svg`

// Orders module
export const IMG_FOLDER_BLUE_ARROW = `${AWS_S3_URL}/img/orders/folder-blue-arrow.png`

export const MEXICO_AREA_CODE = '52'

export const MINIMUM_AMOUNT = 0.01

export const NEW_ITEM_IMAGE: IImage = { alt: 'newItem', src: `${AWS_S3_URL}/img/icons/plus.svg` }

export const PAGES_RETURNED_BY_CONEKTA_PAYMENTS_API = 20

export const PAYMENT_BRAND_LOGOS: IPaymentBrandLogos = {
  american_express: `${AWS_S3_URL}/img/amex.svg`,
  banorte: `${AWS_S3_URL}/brands/logos/banorte-24px.svg`,
  carnet: `${AWS_S3_URL}/img/carnet.svg`,
  mc: `${AWS_S3_URL}/img/mastercard.svg`,
  oxxo: `${AWS_S3_URL}/img/oxxo-pay-2019.svg`,
  spei: `${AWS_S3_URL}/img/spei.svg`,
  discover: `${AWS_S3_URL}/brands/logos/discover-24px.svg`,
  visa: `${AWS_S3_URL}/img/visa.svg`,
  chargeCash: `${AWS_S3_URL}/img/conekta-logo-symbol.svg`,
  sevenEleven: `${AWS_S3_URL}/brands/logos/seven-eleven.svg`,
  farmaciasAhorro: `${AWS_S3_URL}/brands/logos/farmacias-ahorro.svg`,
  datalogic: `${AWS_S3_URL}/brands/logos/datalogic-brands-24px.svg`,
  pespay: `${AWS_S3_URL}/brands/logos/pespay.svg`,
  bodegaaurrera: `${AWS_S3_URL}/brands/logos/bodegaaurrera-24x24.svg`,
  sams: `${AWS_S3_URL}/brands/logos/sams-24x24.svg`,
  superkiosko: `${AWS_S3_URL}/brands/logos/superkiosko-24x24.svg`,
  waldos: `${AWS_S3_URL}/brands/logos/waldos-24x24.svg`,
  wallmart: `${AWS_S3_URL}/brands/logos/wallmart-24x24.svg`
}

export const PAYMENT_BRAND_LOGOS_24PX: IPaymentBrandLogos = {
  american_express: `${AWS_S3_URL}/brands/logos/amex-24px.svg`,
  banorte: `${AWS_S3_URL}/brands/logos/banorte-24px.svg`,
  carnet: `${AWS_S3_URL}/brands/logos/carnet-24px.svg`,
  mc: `${AWS_S3_URL}/brands/logos/master-card-24px.svg`,
  oxxo: `${AWS_S3_URL}/brands/logos/oxxo-24px.svg`,
  spei: `${AWS_S3_URL}/brands/logos/spei-24px.svg`,
  discover: `${AWS_S3_URL}/brands/logos/discover-24px.svg`,
  visa: `${AWS_S3_URL}/brands/logos/visa-24px.svg`,
  klarna_bnpl: `${AWS_S3_URL}/brands/logos/logo-klarna.svg`,
  creditea_bnpl: `${AWS_S3_URL}/brands/logos/logo-creditea.svg`,
  aplazo_bnpl: `${AWS_S3_URL}/brands/logos/logo-aplazo.svg`,
  chargeCash: `${AWS_S3_URL}/img/conekta-logo-symbol.svg`,
  sevenEleven: `${AWS_S3_URL}/brands/logos/seven-eleven.svg`,
  farmaciasAhorro: `${AWS_S3_URL}/brands/logos/farmacias-ahorro.svg`,
  datalogic: `${AWS_S3_URL}/brands/logos/datalogic-brands-24px.svg`,
  pespay: `${AWS_S3_URL}/brands/logos/pespay.svg`,
  bodegaaurrera: `${AWS_S3_URL}/brands/logos/bodegaaurrera-24x24.svg`,
  sams: `${AWS_S3_URL}/brands/logos/sams-24x24.svg`,
  superkiosko: `${AWS_S3_URL}/brands/logos/superkiosko-24x24.svg`,
  waldos: `${AWS_S3_URL}/brands/logos/waldos-24x24.svg`,
  wallmart: `${AWS_S3_URL}/brands/logos/wallmart-24x24.svg`,
  bbva: `${AWS_S3_URL}/brands/logos/bbva.svg`,
  bbva_pay_by_bank: `${AWS_S3_URL}/brands/logos/bbva.svg`,
  bbva_cash_in: `${AWS_S3_URL}/brands/logos/bbva.svg`,
  pay_by_bank: `${AWS_S3_URL}/brands/logos/bbva.svg`,
  apple: `${AWS_S3_URL}/brands/logos/apple.svg`,
  apple_pay: `${AWS_S3_URL}/brands/logos/apple.svg`,
  apple_pay_visa: `${AWS_S3_URL}/brands/logos/visa-24px.svg`
}

export const PAYMENT_BRANDS_FORMATTED_FOR_IMAGE = (logoSrcUrls: IPaymentBrandLogos): IPaymentBrands => ({
  american_express: {
    alt: 'American Express',
    className: 'american_express',
    src: logoSrcUrls.american_express
  },
  banorte: {
    alt: 'Banorte',
    className: 'banorte',
    src: logoSrcUrls.banorte
  },
  bbva: {
    alt: 'BBVA',
    className: 'bbva',
    src: logoSrcUrls.bbva
  },
  carnet: {
    alt: 'Carnet',
    className: 'carnet',
    src: logoSrcUrls.carnet
  },
  bankTransfer: {
    alt: 'SPEI',
    className: 'spei',
    src: logoSrcUrls.spei
  },
  bnpl: {
    alt: 'Bnpl',
    className: 'bnpl',
    src: '' // this is empty because bnpl have multiple providers
  },
  discover: {
    alt: 'Discover',
    className: 'discover',
    src: logoSrcUrls.discover
  },
  pay_by_bank: {
    alt: 'Pago en banco',
    className: 'paybybank',
    src: logoSrcUrls.pay_by_bank
  },
  bbva_cash_in: {
    alt: 'Efectivo',
    className: 'bbva_cash_in',
    src: logoSrcUrls.bbva_cash_in
  },
  bbva_pay_by_bank: {
    alt: 'Pago en banco',
    className: 'paybybank',
    src: logoSrcUrls.bbva_pay_by_bank
  },
  klarna_bnpl: {
    alt: 'Klarna',
    className: 'klarna',
    src: logoSrcUrls.klarna_bnpl
  },
  creditea_bnpl: {
    alt: 'Creditea',
    className: 'creditea',
    src: logoSrcUrls.creditea_bnpl
  },
  aplazo_bnpl: {
    alt: 'Aplazo',
    className: 'aplazo',
    src: logoSrcUrls.aplazo_bnpl
  },
  oxxo: {
    alt: 'Oxxo',
    className: 'oxxo',
    src: logoSrcUrls.oxxo
  },
  cash: {
    alt: 'Cash',
    className: 'cash',
    src: logoSrcUrls.chargeCash
  },
  cashout: {
    alt: 'Cashout',
    className: 'cashout',
    src: logoSrcUrls.chargeCash
  },
  oxxo_cash: {
    alt: 'Oxxo',
    className: 'oxxo_cash',
    src: logoSrcUrls.oxxo
  },
  oxxo_recurrente: {
    alt: 'Oxxo',
    className: 'oxxo_recurrente',
    src: logoSrcUrls.oxxo
  },
  mastercard: {
    alt: 'Mastercard',
    className: 'mastercard',
    src: logoSrcUrls.mc
  },
  mc: {
    alt: 'Mastercard',
    className: 'mc',
    src: logoSrcUrls.mc
  },
  spei: {
    alt: 'SPEI',
    className: 'spei',
    src: logoSrcUrls.spei
  },
  visa: {
    alt: 'visa',
    className: 'visa',
    src: logoSrcUrls.visa
  },
  sevenEleven: {
    alt: 'Seven Eleven',
    className: 'seven_eleven',
    src: logoSrcUrls.sevenEleven
  },
  farmaciasAhorro: {
    alt: 'Farmacias Ahorro',
    className: 'farmacias_ahorro',
    src: logoSrcUrls.farmaciasAhorro
  },
  cash_refund: {
    alt: 'Cashout',
    className: 'cashout',
    src: logoSrcUrls.chargeCash
  },
  // cash_in is used too for datalogic cash payments, we need a refactor to unify this
  cash_in: {
    alt: 'Datalogic',
    className: 'datalogic',
    src: logoSrcUrls.datalogic
  },
  datalogic: {
    alt: 'Datalogic',
    className: 'datalogic',
    src: logoSrcUrls.datalogic
  },
  pespay: {
    alt: 'Pespay',
    className: 'pespay',
    src: logoSrcUrls.pespay
  },
  bodegaaurrera: {
    alt: 'Bodega aurrera',
    className: 'bodegaaurrera',
    src: logoSrcUrls.bodegaaurrera
  },
  sams: {
    alt: 'sams',
    className: 'sams',
    src: logoSrcUrls.sams
  },
  superkiosko: {
    alt: 'superkiosko',
    className: 'superkiosko',
    src: logoSrcUrls.superkiosko
  },
  waldos: {
    alt: 'waldos',
    className: 'waldos',
    src: logoSrcUrls.waldos
  },
  wallmart: {
    alt: 'wallmart',
    className: 'wallmart',
    src: logoSrcUrls.wallmart
  },
  apple: {
    alt: 'Apple',
    className: 'apple',
    src: logoSrcUrls.apple
  },
  apple_pay: {
    alt: 'Apple',
    className: 'apple',
    src: logoSrcUrls.apple_pay
  },
  apple_pay_visa: {
    alt: 'visa',
    className: 'visa',
    src: logoSrcUrls.visa
  }
})

export const PAYMENT_BRANDS: IPaymentBrands = PAYMENT_BRANDS_FORMATTED_FOR_IMAGE(PAYMENT_BRAND_LOGOS)
export const PAYMENT_BRANDS_24PX: IPaymentBrands = PAYMENT_BRANDS_FORMATTED_FOR_IMAGE(PAYMENT_BRAND_LOGOS_24PX)

export const PAYMENT_LINKS_FILTERS_BY_COMPANY_INITIAL: IPaymentLinksPagination = {
  from: undefined,
  name: undefined,
  page: 1,
  recurrent: undefined,
  timeZoneOffset: undefined,
  to: undefined
}

export const PAYMENT_LINK_STATUS_COLORS: IPaymentStatusColors = {
  Cancelled: 'red',
  Expired: 'gray',
  Finalized: 'green',
  Issued: 'blue'
}

// Comprehensive list of possible payment statuses
// based on getPaymentStatus in payment.ts (total 14)
export const PAYMENT_STATUS = {
  Expired: 'Expired',
  canceled: 'canceled',
  cancelled: 'cancelled',
  chargeBack: 'chargeback',
  declined: 'declined',
  declined_by_3ds: 'declined_by_3ds',
  expired: 'expired',
  noCharge: 'no_charge',
  paid: 'paid',
  partialRefund: 'partially_refunded',
  pendingConfirmation: 'pending_confirmation',
  pendingPayment: 'pending_payment',
  preAuthorized: 'pre_authorized',
  refunded: 'refunded',
  suspectedFraud: 'suspected_fraud',
  underFraudReview: 'under_fraud_review',
  voided: 'voided'
}

export const PAYMENT_STATUS_COLORS: IPaymentStatusColors = {
  canceled: 'red',
  charged_back: 'red',
  declined: 'red',
  expired: 'gray',
  no_charge: 'gray',
  paid: 'green',
  partially_refunded: 'blue',
  pending: 'yellow',
  pending_confirmation: 'yellow',
  pending_payment: 'yellow',
  pre_authorized: 'yellow',
  refunded: 'blue',
  suspected_fraud: 'orange',
  under_fraud_review: 'orange',
  voided: 'red',
  declined_by_3ds: 'orange'
}

export const PAYMENT_TYPE = {
  credit: 'credit',
  debit: 'debit',
  oxxoBatch: 'oxxo',
  oxxoPay: 'oxxo_cash',
  oxxoRecurrent: 'oxxo_recurrent',
  spei: 'spei',
  bnpl: 'bnpl',
  payByBank: 'pay_by_bank',
  apple: 'apple_pay'
}

export const CHECKOUT_REQUEST_TYPE = {
  PAYMENT_LINK: 'PaymentLink',
  HOSTED_PAYMENT: 'HostedPayment',
}

export const PAYMENT_TYPE_CARD = [PAYMENT_TYPE.credit, PAYMENT_TYPE.debit]

export const REFUNDABLE_STATUS = [PAYMENT_STATUS.paid, PAYMENT_STATUS.partialRefund]

export const REFUNDABLE_TYPE = [
  PAYMENT_TYPE.credit,
  PAYMENT_TYPE.debit,
  PAYMENT_TYPE.bnpl,
  PAYMENT_TYPE.oxxoBatch,
  PAYMENT_TYPE.oxxoPay,
  PAYMENT_TYPE.oxxoRecurrent
]

export const REFUNDED_STATUS = [PAYMENT_STATUS.refunded, PAYMENT_STATUS.partialRefund]

export const RISK_VALIDATION_STATUSES = {
  changeMcc: 'CHANGEMCC',
  rejected: 'REJECTED',
  review: 'REVIEW'
}

export const SEQUENCE_STEPS: TSecuenceSteps[] = ['REQUESTED', 'SUCCESSFUL', 'FAILED', 'RESET']

export const SHOPPING_CART_IMG = `${AWS_S3_URL}/img/icons/shopping-cart-24px.svg`
export const SHOPPING_CART_OUTLINE_IMG = `${AWS_S3_URL}/img/icons/shopping_cart_outline_sb_24px.svg`

export const STEP_REQUESTED_INDEX = 0
export const STEP_SUCCESSFUL_INDEX = 1
export const STEP_FAILED_INDEX = 2
export const STEP_RESET_INDEX = 3

const t = i18next.t.bind(i18next)

export const TEST_MODE_DEFAULT = false

export const TOTAL_COMPANY_STEPS_REGISTER = 5

export const WIZARD_STAGES_ID = {
  additional: 'in_process_additional_info',
  active: 'active',
  contract: 'in_process_contract',
  evaluation: 'in_process_evaluation',
  integration: 'in_process_integration',
  plan: 'in_process_profile_plan',
  information: 'in_process_profile_information',
  documents: 'in_process_profile_documents',
  life: 'in_process_proof_of_life',
  confirmation: 'in_process_contract'
}

// Don't order this list alphabetically, the sequence follows a business rule
export const WIZARD_STAGES_SEQUENCE: Array<string> = [
  WIZARD_STAGES_ID.information,
  WIZARD_STAGES_ID.documents,
  WIZARD_STAGES_ID.additional,
  WIZARD_STAGES_ID.life,
  WIZARD_STAGES_ID.contract
]

export enum CompanyStatus {
  ACTIVE = 'active', // deprecated
  ACTIVATED = 'activated',
  INACTIVE = 'inactive',
  IN_PROCESS = 'inProcess',
  TESTING = 'testing'
}

export enum ETourType {
  PRODUCTION = 'production',
  TESTING = 'testing'
}

export enum EFileFormat {
  PDF = 'pdf',
  XML = 'xml',
  CSV = 'csv',
  XLSX = 'xlsx'
}

export enum EReportType {
  ORDERS = 'orders',
  PAYMENTS = 'payments',
  CASHOUT = 'payout_orders',
  LEDGER_ENTRIES = 'ledger_entries',
  TRANSFER_DETAIL = 'transfer_detail',
  INVOICE_DETAIL = 'invoice_detail',
  ACCOUNT_STATUS = 'account_status'
}

export enum EDownloadType {
  INVOICE = 'invoice',
  REPORT = 'report',
  DISABLED = 'disabled'
}

export const ENV_MODE_KEY = 'ENV_MODE'

export const INTEGRATION_TYPE = {
  PLUGINS: 'https://developers.conekta.com/page/plugins',
  INTEGRATION: 'https://developers.conekta.com/reference/autenticaci%C3%B3n'
}

export enum EFileType {
  PDF = 'application/pdf'
}

export enum EFileClassification {
  PROOF_OF_TAX_IDENTIFICATION = '4127f944-12bc-4535-a84d-63cec1af5c4a',
  officialIDFront = '583402ef-93c9-42b9-a3af-7e509b703875',
  officialIDBack = '5d136eea-d3de-4b8a-b085-9a524ee0fbd3',
  proofOfAddress = '59562f9f-b4b9-492a-bf13-6547be3b58ee'
}

export const ACCOUNT_EXECUTIVE_MAX_DRAFT_COMPANIES = 20

export enum ONBOARDING_URL {
  CREATE_COMPANY = '/onboarding/create-company',
  BLUEBOOK = '/onboarding/bluebook',
  WIZARD = '/onboarding/company-wizard'
}

export enum PANEL_URL {
  DASHBOARD = '/dashboard',
  ANTIFRAUD = '/antifraud',
  LISTS = '/antifraud/lists',
  RULES = '/antifraud/rules',
  SECURE_3DS = '/antifraud/secure3ds',
  SUBSCRIPTIONS = '/subscriptions',
  CUSTOMERS = '/subscriptions/customers',
  PLANS = '/subscriptions/plans',
  PARTNERS = '/partners',
  ORDERS = '/orders',
  PAYMENTS = 'transactions/payments'
}

export const IVA = 0.16

export const customSize = 4194304
export const filesTypesOfficialID = { 'image/jpeg': ['.jpeg'], 'image/png': ['.png'] }
export const bytesInMb = 1048576

export const ENVIRONMENTS = {
  DEV: 'development',
  PROD: 'production'
}

export enum EOnboardingFlow {
  AE = 'ae',
  SMB = 'smb',
  ENTERPRISE = 'enterprise'
}

export enum EPersonType {
  PHYSICAL = 'Persona Fisica',
  MORAL = 'Persona moral'
}

export enum ECompanyType {
  LEGAL = 'legal',
  FISCAL = 'fiscal'
}

export const disableDragFilesTitles = ['"id_legal_representative"']

export const showModalFileTitles = ['id_legal_representative']

export const COLORS = {
  surface: '#FDFEFF',
  neutral5: '#D8D8E8',
  neutral8: '#585987',
  colorblue007: '#090E94',
  extralightBlue: '#E5EDFF',
  neutral2: '#F7F8FD',
  neutral7: '#8D8FBA'
}

export const URL_DIGITAL_FEMSA = process.env.URL_DIGITAL_FEMSA || ''

export const ANTIFRAUD_LIST_ID = process.env.ANTIFRAUD_LIST_ID || ''

export const UNKNOWN_VALUE = 'unknown'

export const TOAST_PROPS_DEFAULT = (status: ToastAlertType, t: TFunction): IToastAlert => {
  return {
    text: t(`toastAlert.${status}.text`),
    title: t(`toastAlert.${status}.title`),
    type: status
  }
}

export enum FEATURE_FLAGS {
  CARDS_SMART_RETRY = 'CardsSmartRetryModule',
  PAYMENTS_LAST4 = 'PaymentsLast4Module'
}
